import * as R from 'ramda'

import {
  RugbyUnionAttributeId,
  RugbyUnionEventStatusId,
  RugbyUnionIncidentId,
} from '@collector/sportsapi-client-legacy'

import {
  BlockedIncidentsMap,
  getPitchIncidentIds,
  PitchIncidents,
  PitchIncidentsConfig,
} from '../../../configuration'

const pitchIncidents: PitchIncidents<
  RugbyUnionIncidentId,
  RugbyUnionAttributeId
> = {
  gridClass: 'grid-cols-12 @2md:grid-cols-6',
  incidentButtonData: [
    {
      incidentId: RugbyUnionIncidentId.Try,
      class: 'col-start-1 col-span-6 row-1 @2md:col-span-6',
    },
    {
      incidentId: RugbyUnionIncidentId.ConversionAttempt,
      class: 'col-span-6 row-1 @2md:col-span-3 @2md:row-3',
    },
    {
      incidentId: RugbyUnionIncidentId.DropGoal,
      class: 'col-span-4 row-2 @2md:col-span-3 @2md:row-2',
    },
    {
      incidentId: RugbyUnionIncidentId.DropGoalMissed,
      class: 'col-span-4 row-2 @2md:col-span-3 @2md:row-2',
    },
    {
      incidentId: RugbyUnionIncidentId.PenaltyAwarded,
      class: 'col-span-4 row-2 @2md:col-span-3 @2md:row-3',
    },
    {
      incidentId: RugbyUnionIncidentId.Offload,
      class: 'col-span-3 row-3 @2md:col-span-3 @2md:row-5',
      name: 'Off\u200Bload',
    },
    {
      incidentId: RugbyUnionIncidentId.GoallineDropout,
      class: 'col-span-3 row-3 @2md:col-span-3 @2md:row-4',
      name: 'Goal\u200Bline drop',
    },
    {
      incidentId: RugbyUnionIncidentId.Turnover,
      class: 'col-span-3 row-3 @2md:col-span-3 @2md:row-6',
      name: 'Turn\u200Bover',
    },
    {
      incidentId: RugbyUnionIncidentId.Kick,
      class: 'col-span-3 row-3 @2md:col-span-3 @2md:row-4',
    },
    {
      incidentId: RugbyUnionIncidentId.Foul,
      class: 'col-span-3 row-4 @2md:col-span-3 @2md:row-6',
    },
    {
      incidentId: RugbyUnionIncidentId.FreeKick,
      class: 'col-span-3 row-4 @2md:col-span-3 @2md:row-5',
      name: 'Free Kick',
    },
    {
      incidentId: RugbyUnionIncidentId.Scrum,
      class: 'col-span-3 row-4 @2md:col-span-2 @2md:row-7',
    },
    {
      incidentId: RugbyUnionIncidentId.Maul,
      class: 'col-span-3 row-4 @2md:col-span-2 @2md:row-7',
    },
    {
      incidentId: RugbyUnionIncidentId.PenaltyTry,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-8',
      name: 'Pena\u200Blty\u00a0try',
    },
    {
      incidentId: RugbyUnionIncidentId.Cards,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-8',
      name: 'Cards',
      onClick: async (_, opts) => {
        if (!opts) return
        const { incidentId, participantTeamId, setIncident } = opts
        await setIncident({
          incidentId,
          participantTeamId,
        })
      },
    },
    {
      incidentId: RugbyUnionIncidentId.SubstitutionIn,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-8',
      name: 'Subs',
    },
    {
      incidentId: RugbyUnionIncidentId.Lineout,
      class: 'col-span-3 row-5 @2md:col-span-2 @2md:row-7',
      name: 'Line\u200Bout',
    },
  ],
}
const eventStatusToBlockedPitchIncidentsTextMap = new Map<
  RugbyUnionEventStatusId,
  string
>([
  [RugbyUnionEventStatusId.NotStarted, 'start the match'],
  [RugbyUnionEventStatusId.StartDelayed, 'start the match'],
  [RugbyUnionEventStatusId.WaitingForPenalty, 'start the penalty shootout'],
])

const pitchIncidentIds = getPitchIncidentIds(pitchIncidents.incidentButtonData)

const pitchIncidentIdsWithoutSubstitutionIn: Set<RugbyUnionIncidentId> =
  new Set(
    R.without([RugbyUnionIncidentId.SubstitutionIn], [...pitchIncidentIds]),
  )

const blockedPitchIncidentsMap: BlockedIncidentsMap<
  RugbyUnionEventStatusId,
  RugbyUnionIncidentId
> = new Map([
  [RugbyUnionEventStatusId.Abandoned, pitchIncidentIds],
  [RugbyUnionEventStatusId.Cancelled, pitchIncidentIds],
  [RugbyUnionEventStatusId.NotStarted, pitchIncidentIds],
  [RugbyUnionEventStatusId.StartDelayed, pitchIncidentIds],
  [RugbyUnionEventStatusId.Postponed, pitchIncidentIds],
  [RugbyUnionEventStatusId.Halftime, pitchIncidentIdsWithoutSubstitutionIn],
  [
    RugbyUnionEventStatusId.WaitingForExtratime,
    pitchIncidentIdsWithoutSubstitutionIn,
  ],
  [
    RugbyUnionEventStatusId.ExtratimeHalftime,
    pitchIncidentIdsWithoutSubstitutionIn,
  ],
  [RugbyUnionEventStatusId.WaitingForPenalty, pitchIncidentIds],
  [
    RugbyUnionEventStatusId.Penalties,
    new Set(
      R.without(
        [
          RugbyUnionIncidentId.PenaltyKickGoal,
          RugbyUnionIncidentId.PenaltyKickMissed,
          RugbyUnionIncidentId.PenaltyAwarded,
        ],
        [...pitchIncidentIds],
      ),
    ),
  ],
  [RugbyUnionEventStatusId.FinishedAfterExtratime, pitchIncidentIds],
  [RugbyUnionEventStatusId.FinishedAfterPenalties, pitchIncidentIds],
  [RugbyUnionEventStatusId.FinishedAwardedWin, pitchIncidentIds],
  [RugbyUnionEventStatusId.FinishedRegularTime, pitchIncidentIds],
  [RugbyUnionEventStatusId.Interrupted, pitchIncidentIds],
  [RugbyUnionEventStatusId.ToFinish, pitchIncidentIds],
])

export const pitchIncidentsConfig: PitchIncidentsConfig<
  RugbyUnionEventStatusId,
  RugbyUnionIncidentId,
  RugbyUnionAttributeId
> = {
  pitchIncidents,
  blockedPitchIncidentsMap,
  eventStatusToBlockedPitchIncidentsTextMap,
}
